import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { financiamentosApi, veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { StatusVeiculo, TipoDespesaVeiculo, TIPO_DESPESA_VEICULO_LABEL } from '../../lib/enums'
import { formatDate, formatMoney } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import type { DespesaVeiculo, SalvarDespesaVeiculoRequest } from '../../types'

import { FiltroPeriodo } from './FiltroPeriodo'
import { PagamentoVeiculoForm } from './PagamentoVeiculoForm'
import { montarPagamento, pagamentoValido, pagamentoVazio, type PagamentoForm } from './pagamentoUtils'
import { formatKm, hojeIso, invalidarVeiculo, lerNumero, soData } from './veiculoUtils'

const TIPOS = Object.values(TipoDespesaVeiculo)

export function DespesasPage() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const modulo = useRecurso('veiculos.modulo')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [formAberto, setFormAberto] = useState(false)
  const [editando, setEditando] = useState<DespesaVeiculo | undefined>()
  const [excluindo, setExcluindo] = useState<DespesaVeiculo | null>(null)

  const { data: v, isLoading: carregandoV, isError: erroV, error } = useQuery({
    queryKey: ['veiculos', id], queryFn: () => veiculosApi.obter(id), enabled: !!id && modulo.habilitado,
  })
  const { data: itens = [], isLoading, isError } = useQuery({
    queryKey: ['veiculos', id, 'despesas', de, ate],
    queryFn: () => veiculosApi.despesas.list(id, { de: de || undefined, ate: ate || undefined }),
    enabled: !!id && modulo.habilitado,
  })

  const remover = useMutation({
    mutationFn: (d: DespesaVeiculo) => veiculosApi.despesas.remover(id, d.id),
    onSuccess: () => { invalidarVeiculo(qc); toast.success('Despesa excluída.'); setExcluindo(null) },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!modulo.habilitado) return <><PageHeader title="Despesas do veículo" /><BloqueioRecurso mensagem="O módulo de veículos não está incluído no seu plano." /></>
  if (carregandoV) return <><PageHeader title="Despesas do veículo" /><Card><LoadingState /></Card></>
  if (erroV || !v) return <><PageHeader title="Despesas do veículo" /><Card><ErrorState message={(error as Error)?.message || 'Veículo não encontrado.'} /></Card></>

  const podeAgir = v.podeEditar && v.status === StatusVeiculo.Ativo
  const abrir = (d?: DespesaVeiculo) => { setEditando(d); setFormAberto(true) }
  const total = itens.reduce((s, d) => s + d.valor, 0)

  return (
    <>
      <PageHeader title="Despesas do veículo" />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{v.apelido}</h3>
          <p className="text-13 text-muted">{v.modelo.marca} {v.modelo.modelo} · {formatKm(v.kmAtual)}</p>
        </div>
        {podeAgir && <Button icon="ri-add-line" onClick={() => abrir()}>Nova despesa</Button>}
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <FiltroPeriodo de={de} ate={ate} onChange={(d, a) => { setDe(d); setAte(a) }} />
        {itens.length > 0 && <p className="text-13 text-muted">{itens.length} despesa(s) · <span className="font-medium text-white">{formatMoney(total)}</span></p>}
      </div>

      <Card>
        {isLoading ? <LoadingState /> : isError ? <ErrorState message="Não foi possível carregar as despesas." /> : itens.length === 0 ? (
          <EmptyState icon="ri-money-dollar-circle-line" message={de || ate ? 'Nenhuma despesa no período.' : 'Nenhuma despesa registrada.'}
            action={podeAgir ? <Button variant="soft-primary" icon="ri-add-line" onClick={() => abrir()}>Registrar a primeira</Button> : undefined} />
        ) : (
          <Table>
            <THead><tr><Th>Data</Th><Th>Tipo</Th><Th className="text-right">Valor</Th><Th>Vencimento</Th><Th>Pagamento</Th><Th className="w-24" /></tr></THead>
            <tbody>
              {itens.map((d) => (
                <tr key={d.id} className="border-t border-border/70">
                  <Td>{formatDate(d.data)}</Td>
                  <Td>
                    {TIPO_DESPESA_VEICULO_LABEL[d.tipo]}
                    {d.observacao && <p className="max-w-xs truncate text-2xs text-muted">{d.observacao}</p>}
                  </Td>
                  <Td className="text-right font-medium text-white">{formatMoney(d.valor)}</Td>
                  <Td>{d.vencimento ? formatDate(d.vencimento) : '—'}</Td>
                  <Td>
                    {d.financiamentoId ? <Badge variant="info" className="normal-case">Financiamento</Badge> : d.numParcelas > 1 ? `${d.numParcelas}x de ${formatMoney(d.valor / d.numParcelas)}` : 'Cota única'}
                  </Td>
                  <Td>
                    {podeAgir && (
                      <div className="flex justify-end">
                        <IconButton icon="ri-edit-line" label="Editar" onClick={() => abrir(d)} />
                        <IconButton icon="ri-delete-bin-line" label="Excluir" tone="danger" onClick={() => setExcluindo(d)} />
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <p className="mt-6 text-xs text-muted"><Link to={`/veiculos/${v.id}`} className="hover:text-primary"><i className="ri-arrow-left-line" aria-hidden /> Voltar para {v.apelido}</Link></p>

      {formAberto && <DespesaFormModal veiculoId={v.id} despesa={editando} onClose={() => setFormAberto(false)} />}

      <Modal open={!!excluindo} onClose={() => setExcluindo(null)} size="sm" title="Excluir despesa"
        footer={<><Button variant="light" onClick={() => setExcluindo(null)}>Cancelar</Button><Button variant="danger" icon="ri-delete-bin-line" loading={remover.isPending} onClick={() => excluindo && remover.mutate(excluindo)}>Excluir</Button></>}>
        {excluindo && (
          <p className="text-13 text-muted">
            Excluir {TIPO_DESPESA_VEICULO_LABEL[excluindo.tipo]} de <span className="text-white">{formatMoney(excluindo.valor)}</span>?
            {excluindo.transacaoId ? ' Os lançamentos ligados também serão excluídos.' : ''}
          </p>
        )}
      </Modal>
    </>
  )
}

function DespesaFormModal({ veiculoId, despesa: d, onClose }: { veiculoId: string; despesa?: DespesaVeiculo; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const editando = !!d
  const [tipo, setTipo] = useState<TipoDespesaVeiculo>(d?.tipo ?? TipoDespesaVeiculo.Ipva)
  const [data, setData] = useState(d ? soData(d.data) : hojeIso())
  const [valor, setValor] = useState(d ? String(d.valor) : '')
  const [vencimento, setVencimento] = useState(d?.vencimento ? soData(d.vencimento) : '')
  const [obs, setObs] = useState(d?.observacao ?? '')
  const [financiamentoId, setFinanciamentoId] = useState(d?.financiamentoId ?? '')
  const [pagamento, setPagamento] = useState<PagamentoForm>(pagamentoVazio())
  const [trocarPagamento, setTrocarPagamento] = useState(!editando)

  const ehFinanciamento = tipo === TipoDespesaVeiculo.Financiamento
  const { data: financiamentos = [] } = useQuery({
    queryKey: ['financiamentos', 'lista-veiculo'], queryFn: () => financiamentosApi.list().then((r) => r.itens), enabled: ehFinanciamento,
  })
  const v = lerNumero(valor) ?? 0
  const referencia = ehFinanciamento && !!financiamentoId
  const pagamentoOk = referencia || (editando && !trocarPagamento) || pagamentoValido(pagamento)
  const valido = v > 0 && !!data && pagamentoOk

  const salvar = useMutation({
    mutationFn: () => {
      const req: SalvarDespesaVeiculoRequest = {
        tipo, data, valor: v, vencimento: vencimento || null, observacao: obs.trim() || null,
        numParcelas: referencia ? 1 : Math.max(1, Math.floor(Number(pagamento.numParcelas) || 1)),
        financiamentoId: referencia ? financiamentoId : null,
        pagamento: referencia || (editando && !trocarPagamento) ? null : montarPagamento(pagamento),
      }
      return d ? veiculosApi.despesas.atualizar(veiculoId, d.id, req) : veiculosApi.despesas.criar(veiculoId, req)
    },
    onSuccess: () => { invalidarVeiculo(qc); toast.success(editando ? 'Despesa atualizada.' : 'Despesa registrada.'); onClose() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open onClose={onClose} size="lg" title={editando ? 'Editar despesa' : 'Nova despesa'}
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}>
      <div className="space-y-4">
        <Select label="Tipo" value={tipo} onChange={(e) => { setTipo(Number(e.target.value) as TipoDespesaVeiculo); setFinanciamentoId('') }}>
          {TIPOS.map((t) => <option key={t} value={t}>{TIPO_DESPESA_VEICULO_LABEL[t]}</option>)}
        </Select>
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Valor (R$)" type="number" inputMode="decimal" min={0} step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} autoFocus />
          <Input label="Data" type="date" value={data} onChange={(e) => setData(e.target.value)} />
          <Input label="Vencimento (opcional)" type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
        </div>
        {ehFinanciamento && (
          <Select label="Financiamento existente (opcional)" value={financiamentoId} onChange={(e) => setFinanciamentoId(e.target.value)}>
            <option value="">Nenhum (lançar a despesa)</option>
            {financiamentos.map((f) => <option key={f.id} value={f.id}>{f.descricao}{f.instituicao ? ` · ${f.instituicao}` : ''}</option>)}
          </Select>
        )}
        {referencia ? (
          <p className="rounded bg-info/10 px-3 py-2.5 text-13 text-info"><i className="ri-information-line" aria-hidden /> A despesa só referencia o financiamento: nenhum lançamento novo é criado.</p>
        ) : (
          <div className="space-y-2">
            <span className="form-label">Pagar com</span>
            {editando && (
              <label className="flex items-center gap-2 text-13 text-white">
                <input type="checkbox" checked={trocarPagamento} onChange={(e) => setTrocarPagamento(e.target.checked)} /> Trocar a forma de pagamento
              </label>
            )}
            {trocarPagamento && <PagamentoVeiculoForm valor={pagamento} onChange={setPagamento} comParcelas valorTotal={v} />}
          </div>
        )}
        <Textarea label="Observação (opcional)" rows={2} value={obs} onChange={(e) => setObs(e.target.value)} />
      </div>
    </Modal>
  )
}
